/** 판정 로직 — "이 상태에서 무엇이 가능한가".
 *
 * 이것이 화면 조건문에 흩어지면 앱과 웹이 다르게 판단한다. 예를 들어 "재시도 버튼을
 * 언제 보여주는가"를 양쪽이 각자 쓰면, 한쪽은 실패한 장이 있을 때 보여주고 다른 쪽은
 * 작업이 FAILED 일 때만 보여주는 식으로 갈라진다. 판정은 여기 한 곳에서만 한다.
 */

import type {
  DetailJobStatus,
  DetailJobView,
  JobFailureCode,
  JobImageView,
  RecallBlockReason,
} from "./domain";
import { TERMINAL_JOB_STATUSES } from "./domain";
import { isWithinSourceRetention, type D1DateTime } from "./retention";

const TERMINAL: ReadonlySet<string> = new Set<string>(TERMINAL_JOB_STATUSES);

/** **작업 한 건이 목록에 보이는 기간(일).** 7일이 지나면 `GET /api/jobs` 가 그 작업을 빼고
 *  준다(2026-09-14 사용자 확정).
 *
 * ⛔ **보관 기간이 아니다. 데이터는 그대로 둔다.** 이 값이 정하는 것은 **목록에 보이는
 * 동안**이고, 원본·결과물·청구 내역은 지우지 않는다. 파기 배치도 없다. 그래서 화면 문구에
 * `지워집니다` 를 쓰면 거짓이 된다. 참인 문장은 `목록에서 보이지 않습니다` 계열이다.
 * 화면 여러 곳이 아직 말하는 `보관 기간은 정해지지 않았습니다` 는 **여전히 참이다.**
 * 정해진 것은 노출 기간 하나뿐이다.
 *
 * 값을 쓰는 자리는 **작업을 만들 때 한 번**이다(`apps/api/src/lib/jobs.ts` 의 `createJob`
 * 이 `retentionExpiresAt` 에 박는다). `createdAt` 에서 매번 계산하지 않는 이유가 둘이다.
 *   ① 이 상수를 나중에 바꿔도 **이미 만든 작업이 거짓말하지 않는다**(0005:71 이 세션
 *      만료에 같은 이유를 적어 뒀다)
 *   ② 멤버십처럼 기간을 늘려 주는 것이 붙으면 **그 행의 값을 미는 것만으로 끝난다** */
export const JOB_LIST_VISIBLE_DAYS = 7;

/** 서버가 더 이상 이 작업을 건드리지 않는 상태. 폴링을 멈출 기준이기도 하다.
 *
 * `EXPORTED`·`DISCARDED` 도 종착이다 — 빠뜨리면 화면이 끝난 작업을 영원히 폴링한다. */
export function isJobSettled(job: DetailJobView): boolean {
  return TERMINAL.has(job.status);
}

/** 서버가 지금 일하고 있는가.
 *
 * ⚠️ `PAID` 는 여기 넣지 않는다. `PROCESSING` 은 "지금 요청이 돌고 있다"는 뜻으로만 써야
 * 이중 실행 가드가 성립하고, `PAID` 는 결제만 끝나고 아직 시작하지 않은 구간이다. */
export function isJobRunning(job: DetailJobView): boolean {
  return job.status === "PROCESSING";
}

/** 결제는 끝났는가. 시작 전(`PAID`)이어도 처리 확약이라 셀러에게는 결제된 작업이다(PRD S3b ②). */
export function isJobPaid(job: DetailJobView): boolean {
  return job.status !== "UPLOADED" && job.status !== "DISCARDED";
}

/** 원본이 파기돼 다시 처리할 수 없는가. 행은 남아 있어도 R2 객체가 없다. */
export function isJobDiscarded(job: Pick<DetailJobView, "status">): boolean {
  return job.status === "DISCARDED";
}

export function doneImages(job: DetailJobView): JobImageView[] {
  return job.images.filter((img) => img.status === "DONE");
}

export function failedImages(job: DetailJobView): JobImageView[] {
  return job.images.filter((img) => img.status === "FAILED");
}

export function pendingImages(job: DetailJobView): JobImageView[] {
  return job.images.filter((img) => img.status === "PENDING");
}

/** 지금 provider 에 나가 있는 장. `PENDING` 과 합쳐야 "아직 결판 안 난 장"이 된다. */
export function processingImages(job: DetailJobView): JobImageView[] {
  return job.images.filter((img) => img.status === "PROCESSING");
}

/** 일부만 실패했는가. **전부 실패는 부분 실패가 아니다** — 화면이 달라진다(A-22 vs A-23).
 * 판정 단위는 배치가 아니라 작업 전체의 장이다. 서버도 `computeJobStatus()` 로 같은 판정을 한다. */
export function isPartialFailure(job: DetailJobView): boolean {
  const failed = failedImages(job).length;
  return failed > 0 && failed < job.images.length;
}

/** 한 장도 만들지 못했는가. 청구 정책(전액 환불)과 A-23 화면의 조건이다. */
export function isTotalFailure(job: DetailJobView): boolean {
  return job.images.length > 0 && failedImages(job).length === job.images.length;
}

/** ⭐ **재이행 판정에 곱해지는 시간 항.** 그 장의 원본 보관 기한(30일)이 지났는가.
 *
 * **법무 `4.9.1`(2)㉡ 이 요구하는 축이 이것이다.** 판정 입력이 **「그 장의 기한 안인가」**
 * 여야 하고 **「원본이 아직 있는가」여서는 안 된다.** 원본 유무로 판정하면 **학습 사본이
 * 원본으로 세어지는 구현이 언제든 생기고**, 그 구현이 한 번이라도 돌면 "학습을 거부해도
 * 제한이 없다"가 그 기간에 대해 거짓이었던 것이 되며 되돌릴 수 없다.
 *
 * ⭐ **이 함수가 필요해진 이유가 `0011` 이다.** 기한 칸이 서기 전에는 기한을 지난 장이
 * 없어서 아래 두 판정이 틀린 답을 내지 않았다. **칸이 서는 순간 곧바로 틀린다.** 30일
 * 배치가 도는 것은 하루 한 번이라 **기한만 지나고 원본은 아직 있는 장이 매일 생기고,
 * 그 장은 원본이 있어서 다시 만들어진다**(`DATA_DICTIONARY` 31.8).
 *
 * ## `undefined` 와 `null` 을 가른다. **뜻이 다르고 답도 다르다**
 *
 * | `image.sourceRetentionExpiresAt` | `now` | 결과 |
 * |---|---|---|
 * | `undefined` (서버가 이 축을 아직 안 준다) | 무관 | **`false`.** 시간 항을 곱하지 않는다 |
 * | 값이 있다 | 없다 | **`true`.** 축은 섰는데 시계가 없다 → 막는다 |
 * | `null` (서버가 모른다고 답했다) | 무관 | **`true`.** 모르면 막는다 |
 * | 값이 있다 | 있다 | `now >= expiresAt` |
 *
 * ⭐ **첫 줄이 이행기를 여는 유일한 칸이고, 그 상태는 서버가 칸을 싣는 날 끝난다.**
 * 그 뒤로는 **`now` 를 안 넘긴 호출이 전부 막는 쪽으로 떨어진다.** 버튼이 사라지므로
 * **눈에 보이고, 조용히 통과하지 않는다.** ⛔ 이 기본값을 뒤집지 마라.
 *
 * ## ⛔ `now` 를 이 파일이 만들지 않는 이유
 *
 * **`retention.ts` 의 규율이다.** 기한 문자열은 SQL(`datetime(...)`)이 만들고
 * `apps/api/src/lib` 전체에 `new Date(` 가 한 줄도 없다. 여기서 `Date` 를 쓰면 **Hermes(RN)
 * 와 Workers 가 같은 문자열을 다르게 읽는 창**이 열린다. **지금을 아는 쪽이 넘긴다.**
 *
 * @param now 지금. D1 이 주는 모양(`YYYY-MM-DD HH:MM:SS`, UTC)이어야 한다. 서버는
 *   `CURRENT_TIMESTAMP` 를, 화면은 **`DetailJobView.servedAt`**(36장)을 쓴다.
 *   ⛔ **화면이 `Date.now()` 로 만들지 마라.** 그 순간 기기 시계를 앞당긴 셀러가 기한 지난
 *   원본에 공급자 호출을 낼 수 있고, ISO 8601 이 섞이면 사전순 비교가 통째로 뒤집힌다. */
export function isSourceRetentionExpired(
  image: Pick<JobImageView, "sourceRetentionExpiresAt">,
  now?: D1DateTime | null,
): boolean {
  // 칸 자체가 응답에 없다 = 이 배포는 아직 이 축을 모른다. 지금까지의 판정을 바꾸지 않는다.
  if (!("sourceRetentionExpiresAt" in image) || image.sourceRetentionExpiresAt === undefined) {
    return false;
  }
  // 여기부터는 축이 선 것이다. **모르면 막는다.**
  if (typeof now !== "string" || now === "") return true;
  return !isWithinSourceRetention(image.sourceRetentionExpiresAt, now);
}

/** 이 장을 다시 시도할 수 있는가.
 *
 * **되살리기도 provider 를 새로 부른다. 우리 원가(장당 ₩27)가 또 나간다**(PRD 4.2, 7.6.4).
 * 그래서 "실패한 장만" 이라는 조건을 여기서 못 박는다. 성공한 장을 다시 거는 경로를 열면
 * 원가가 이유 없이 나가고, 그 동작은 돈을 말하는 시트(`A-25`)를 거쳐야 한다.
 *
 * ⚠️ **되살리기를 셀러에게 다시 청구하는지는 미결이다**(PRD `13.1` O-10). 결제가 선불이라
 * "이미 낸 돈 안"인지 "추가 청구"인지가 안 정해졌다. **여기서 어느 쪽으로도 단정하지
 * 않는다.** 확정된 것은 `cutoutPrice` 가 곱하는 장당 청구가(₩500) 하나뿐이고, 그 값이
 * 되살리기에 또 붙는지는 그 미결이 닫혀야 안다. 횟수 상한도 같은 미결이라 여기서 세지 않는다.
 *
 * 서버 대응은 `jobEndpoints.retryImage`. 서버는 이 판정보다 넓게 받으므로 버튼이 보이는데
 * 거절당하는 경우는 없다. `job` 을 넘기면 처리 중에는 false — 그때 서버가 409 를 준다.
 *
 * 원본이 파기된 작업(`DISCARDED`)에서도 false 다 — 다시 걸면 `SOURCE_MISSING` 으로 실패한다.
 *
 * ⭐ **`now` 를 넘기면 원본 보관 기한(30일)도 본다**(`isSourceRetentionExpired`).
 * **되살리기도 공급자를 새로 부르므로 다시 만들기와 같은 기한에 걸린다.** 법무 ㉡ 이
 * 이 둘을 한 묶음으로 지목한 이유가 그것이다. ⛔ **실패한 장이라고 기한을 봐주지 마라.**
 * 31일째에 되살리면 그 호출이 읽는 것이 기한 지난 원본이고, 그 사실은 화면에 안 보인다.
 * ⭐ **셀러가 `만들지 않기` 로 결판낸 장에서도 false 다**(2026-09-24. `A-24e`. PRD `7.6.7d` ·
 * `docs/DATA_DICTIONARY.md` 34장). 그 장은 **돌아갈 금액이 약속된 장**이라, 우리가 다시 부르면
 * 원가가 나가고 그 약속의 근거가 흔들린다. **막는 자리를 라우트에만 두지 않는 이유**는 이
 * 파일 머리말 그대로다 — 화면이 버튼을 그려 두고 서버가 409 로 끊으면, 셀러는 자기가 방금
 * 고른 선택이 화면에서 지워지지 않은 채 거절당한다.
 * ⛔ **`undefined` 는 막지 않는다.** 서버가 이 칸을 아직 안 주는 이행기가 있고(`domain.ts`),
 * 그때 막으면 **모든 못 만든 장의 되살리기가 통째로 잠긴다.** */
export function canRetryImage(
  image: JobImageView,
  job?: DetailJobView,
  now?: D1DateTime | null,
): boolean {
  if (job && (isJobRunning(job) || isJobDiscarded(job))) return false;
  if (isSourceRetentionExpired(image, now)) return false;
  if (hasDeclinedRecall(image)) return false;
  return image.status === "FAILED";
}

/** **셀러가 이 장을 `만들지 않기` 로 정했는가.** `JobImageView.recallDeclinedAt` 의 유일한
 *  읽는 자리다(PRD `7.6.7d` · `docs/DATA_DICTIONARY.md` 34장).
 *
 * ⛔ **화면이 `image.recallDeclinedAt != null` 을 직접 쓰지 마라.** `undefined`(서버가 아직
 * 안 준다)와 `null`(안 골랐다)이 뜻이 다른데 **둘 다 "안 골랐다"로 떨어져야 한다.** 손으로
 * 쓰면 `!= null` 과 `!== null` 이 갈리고, 뒤엣것은 이행기 내내 **모든 장을 골랐다고 읽는다.** */
export function hasDeclinedRecall(image: JobImageView): boolean {
  return image.recallDeclinedAt != null;
}

/** **이 장을 두고 `A-24e` 가 물어야 하는가.** 시트의 블록 2 가 이 판정으로 선다.
 *
 * `못 만든 장`이고 아직 안 고른 장 하나다. ⛔ **`canRetryImage` 를 곱하지 마라.**
 * 원본이 사라졌거나 규격을 넘어 **되살릴 수 없는 장**(`RecallBlockReason` 의 `SOURCE_GONE` ·
 * `SOURCE_TOO_LARGE`)에도 물음은 서야 한다 — ⭐ **다시 만들 수 없는 장이야말로 안 만들기로
 * 정할 장이다.** 곱하면 그 장이 영영 안 물어지고, 셀러는 **결판이 나지 않는 장**을 들고
 * 30일 배치(`13.1` O-70)만 기다린다.
 *
 * ⚠️ **처리 중인 작업에서는 묻지 않는다.** 이 시트는 **회차가 끝나는 순간**의 것이고
 * (PRD `7.6.7d`), 도는 중에 물으면 방금 실패한 장이 다음 문장에서 대기로 되돌아갈 수 있다
 * (`PROCESSING_RECOVERED`). `job` 을 안 넘기면 그 항을 세지 않는다. */
export function needsRecallDecision(image: JobImageView, job?: DetailJobView): boolean {
  if (job && (isJobRunning(job) || isJobDiscarded(job))) return false;
  return image.status === "FAILED" && !hasDeclinedRecall(image);
}

/** **`A-24e` 의 블록 2 가 늘어놓을 장들.** `못 만든 1장` 의 `1` 이 이 배열의 길이다.
 *
 * ⛔ **`failedImages(job).length` 를 그 자리에 쓰지 마라.** 이미 고른 장까지 세어
 * **셀러가 답한 물음이 회차마다 다시 선다.** 그 둘을 가르는 것이 이 함수의 존재 이유 전부다. */
export function imagesAwaitingRecallDecision(job: DetailJobView): JobImageView[] {
  return job.images.filter((img) => needsRecallDecision(img, job));
}

/** 이 장을 **다시 만들 수 있는가**(`A-25` 시트의 `다시 만들기`).
 *
 * **위 `canRetryImage` 와 짝이고 장 조건이 정확히 반대다.** 그쪽은 `FAILED` 만, 이쪽은
 * `DONE` 만이다. **한 장이 두 판정을 동시에 통과하는 일은 없다.** 그것이 이 둘을 한 함수의
 * 인자 하나로 합치지 않은 이유다 — 합치면 **되살리기**(실패한 장. 셀러에게 또 청구하는지는
 * `13.1` O-10 미결)와 **다시 만들기**(이미 만든 장. **장당 청구가 새로 나가는 것이 확정**)를
 * 호출하는 쪽이 골라야 하고, **고르는 자리가 늘어나면 틀리는 자리가 늘어난다**
 * (PRD `7.6.8` · `7.13` D-62 가 가른 두 동작이다).
 *
 * 서버 대응은 `jobEndpoints.remakeImage`. 처리 중이면 false — 그때 서버가 409
 * (`ALREADY_PROCESSING`)를 준다. 원본이 파기된 작업(`DISCARDED`)에서도 false 다. 그 작업은
 * 결과물까지 접두째 지워져 다시 만들 밑천이 없다(서버 코드는 `JOB_DISCARDED`).
 *
 * ⚠️ **이 판정이 true 라고 "눌러도 좋다"가 되지는 않는다.** 다시 만들기는 돈이 드는 동작이라
 * 확인 시트(`A-25`)를 반드시 거친다. 이 함수가 답하는 것은 **버튼을 그릴지**까지다.
 *
 * ⭐ **`now` 를 넘기면 원본 보관 기한(30일)도 본다**(`isSourceRetentionExpired`).
 *
 * ⛔ **`isJobDiscarded` 를 지우지 마라. 기한 항이 그것을 대신하는 것이 아니다.**
 * 법무 ㉡ 이 지적한 것은 **원본 유무를 기한의 대리값으로 쓰는 것**이고, 그 축은 여전히
 * 참인 별도의 사실이다(파기된 작업은 결과물까지 접두째 없어 다시 만들 밑천이 아예 없다).
 * ⭐ **기한 항을 그 앞에 더하는 것이지 바꾸는 것이 아니다.** 서버 `/remake` 의
 * `ASSETS.head(sourceKey)` 도 같다. 그쪽은 **경합 방어**(그사이 사라진 경우)라 축이 다르고,
 * 둘을 합치면 정확히 ㉡ 이 금지한 모양이 된다. */
export function canRemakeImage(
  image: JobImageView,
  job?: DetailJobView,
  now?: D1DateTime | null,
): boolean {
  if (job && (isJobRunning(job) || isJobDiscarded(job))) return false;
  if (isSourceRetentionExpired(image, now)) return false;
  return image.status === "DONE";
}

/** 이 작업에서 **provider 를 처음 말고 더 부른 횟수**의 합. `A-25` 의 횟수 줄이 읽는다.
 *
 * ```
 * sum(max(0, providerCallCount - 1))
 * ```
 *
 * **왜 `jobRemakeCount` 가 아닌가.** 이 합에는 **실패해서 되살린 장도 들어간다.** 되살리기도
 * 두 번째 호출이라 같은 칸을 올리고, `providerCallCount` 하나로는 "다시 만들어서 는 것"과
 * "되살려서 는 것"을 가를 수 없다. 가르려면 칸을 하나 더 만들어야 하는데, **이 숫자가
 * 답하려는 질문("이번 작업에 원가가 몇 번 더 나갔나")에는 둘 다 들어가는 것이 맞다.**
 * 그래서 **세는 대상의 이름으로 부른다.** `remake` 라고 부르면 D-62 가 지금 떼어내려는
 * 낱말 겹침을 공유 코드 안에 새로 하나 만드는 셈이다.
 *
 * ⚠️ **화면 라벨의 정본은 여기가 아니다.** D-62 는 2026-09-13 에 닫혔고 라벨은
 * `이번 작업에서 더 만든 횟수` 다(PRD `7.5.9` 표 · `7.6.8`). **라벨이 바뀌어도 이 함수
 * 이름은 바뀌지 않는다** — 세는 것이 달라진 것이 아니기 때문이다. 라벨은 `design-manager`
 * 소관이다.
 *
 * ⚠️ **단위는 `장` 이 아니라 `회` 다.** 같은 장을 두 번 다시 만들면 1장에 2회이고,
 * **실패한 호출도 여기 오른다.** 청구는 성공한 호출에만 붙으므로 이 숫자는 청구 장수와
 * 다르다. `A-43` 보조줄의 `다시 만들기 1장` 은 청구 장수라 그쪽이 `장` 인 것이 맞다.
 * **둘을 한 단위로 통일하면 한쪽이 거짓이 된다.**
 *
 * ⚠️ **청구 판정에 쓰지 마라.** 돈의 정본은 `BillingItem`(`kind='CUTOUT_RETRY'` 카운트,
 * `docs/DATA_DICTIONARY.md` 3.1)이고 이것은 화면이 지금 손에 든 `DetailJobView` 로 같은
 * 숫자를 근사하는 것이다. 결제가 붙기 전까지는 청구 행이 아예 없어 이쪽만 답할 수 있다. */
export function jobProviderRecallCount(job: DetailJobView): number {
  return job.images.reduce((n, img) => n + Math.max(0, img.providerCallCount - 1), 0);
}

/** 서버의 `POST /process` 는 PENDING 인 장이 하나도 없으면 409 를 준다.
 * 버튼을 눌러보고 409 를 받는 대신 미리 안다.
 *
 * ⚠️ 처리 중인지도 본다. 장은 끝나야 `DONE` 이 되므로 처리하는 동안에도 대기로 보이고,
 * 남은 대기만 보면 처리 내내 true 라서 두 번 누르면 같은 장이 두 번 과금된다.
 *
 * `READY`·`EXPORTED` 는 막지 않는다 — 끝난 작업에 사진을 더 올려 이어가는 것은 정상 동선이다.
 * `DISCARDED` 만 막는다(원본이 없어 부르면 실패한다). */
export function canStartProcessing(job: DetailJobView): boolean {
  if (isJobRunning(job) || isJobDiscarded(job)) return false;
  return pendingImages(job).length > 0;
}

/** 장별 상태 → 작업 상태·실패 코드. **서버와 화면이 이 함수 하나만 쓴다**(예전엔 갈라졌다).
 *
 * - 처리 안 한 장이 남음 → 결제 전이면 `UPLOADED`, 결제 후면 `PAID`
 * - 한 장도 못 만듦 → `FAILED` + `TOTAL_FAILURE` (전액 환불)
 * - 일부 실패 → `READY` + `PARTIAL_FAILURE` (실패한 장 값만 환불)
 * - 전부 성공 → `READY`
 *
 * `current` 를 넘기면 장 상태로 덮어쓰면 안 되는 두 경우를 지킨다. 안 넘기면 예전과 같다.
 * - `EXPORTED`·`DISCARDED` 는 장 상태보다 위다. 되돌리면 내보낸 작업이 `준비됨`으로 돌아간다
 * - `PAID` 를 `UPLOADED` 로 되돌리면 결제한 작업이 `초안`(결제 전) 칩을 단다
 */
export function computeJobStatus(
  images: Pick<JobImageView, "status">[],
  current?: DetailJobStatus,
): {
  status: DetailJobStatus;
  errorCode: JobFailureCode | null;
} {
  if (current === "EXPORTED" || current === "DISCARDED") {
    return { status: current, errorCode: null };
  }

  // 결제 사실은 장 상태에서 나오지 않는다. 아직 결판이 안 났을 때 되돌아갈 자리다.
  const unsettled: DetailJobStatus = current === "PAID" ? "PAID" : "UPLOADED";

  if (images.length === 0) return { status: unsettled, errorCode: null };

  const failed = images.filter((img) => img.status === "FAILED").length;
  // 나가 있는 장도 대기와 같은 편에 둔다 — 빼면 남은 장이 돌아오기 전에 부분 실패로 확정된다.
  const inFlight = images.filter(
    (img) => img.status === "PENDING" || img.status === "PROCESSING",
  ).length;

  // 아직 결판이 안 났다. 남은 장이 성공하면 부분 실패가 아니게 되므로 코드를 미리 붙이지 않는다.
  if (inFlight > 0) return { status: unsettled, errorCode: null };

  if (failed === images.length) return { status: "FAILED", errorCode: "TOTAL_FAILURE" };
  if (failed > 0) return { status: "READY", errorCode: "PARTIAL_FAILURE" };
  return { status: "READY", errorCode: null };
}

/** 셀러에게 보여줄 결과가 있는가. 일부 성공은 쓸 수 있는 결과다. */
export function hasUsableResult(job: DetailJobView): boolean {
  return doneImages(job).length > 0;
}

// ── 환불 자격 · 다시 부를 수 있는가 ──────────────────────────────────────────
// `docs/DATA_DICTIONARY.md` 28 장. **화면이 각자 쓰면 앱과 웹이 갈리는 판정 셋**이라 이 파일에
// 둔다(머리말). 앞의 둘은 **돈**을 말하고 셋째는 **버튼과 시트**를 말한다.

/** **환불 대상 장.** 곧 `failedImages(job)` 와 **정확히 같은 집합**이다.
 *
 * ⛔ **원인으로 가르지 않는다. 이 함수에 `errorCode` 분기를 넣지 마라.**
 * 사용자 확정(2026-09-21. PRD `7.13` **D-77 ①**): **"실패의 기준은 누끼 등의 작업이
 * 이루어지지 않고, 결과물을 줄 수 없을 때가 실패야."** `NOT_CONFIGURED`(우리 설정) ·
 * `PROVIDER_REJECTED`(사진) · `SOURCE_TOO_LARGE` 가 **전부 같은 취급**이다.
 *
 * **그러면 왜 `failedImages` 를 그냥 쓰지 않고 이름을 하나 더 두는가.** 이 자리가 **분기가
 * 생기려는 자리**이기 때문이다. D-77 ② 가 **같은 코드들로 시트를 가르라고** 정해 놔서,
 * 시트 분기를 쓰던 사람이 돈 쪽에도 같은 분기를 옮겨 적기 쉽다. 이름이 따로 있으면 **그
 * 분기를 넣으려는 사람이 이 주석을 먼저 읽는다.** PRD 가 같은 성질을 이미 적어 뒀다.
 * **"자리를 만들지 않는 것이 요구사항"**(`7.12` 20번 ⓕ).
 *
 * ⚠️ **두 집합이 갈라지는 날은 실패의 정의가 바뀌는 날뿐이다.** 그때 고칠 자리도 여기 하나다. */
export function refundableImages(job: DetailJobView): JobImageView[] {
  return failedImages(job);
}

/** `refundableTotal` 의 답. **금액 하나로 돌려주지 않는 이유가 `unknownCount` 다.** */
export interface RefundableTotal {
  /** 금액을 **아는** 장들의 `refundableAmount` 합. ⛔ 장수 × 단가가 아니다. */
  amount: number;
  /** 환불 대상 장 수 **전체**. `unknownCount` 를 포함한다. */
  imageCount: number;
  /** 그중 **금액을 모르는** 장 수(`refundableAmount` 가 `null` 이거나 아직 안 내려온 장). */
  unknownCount: number;
}

/** **못 만든 장들의 돌아가는 금액 합.** `A-23`·`A-31b`·`A-43b` 가 읽는 숫자다.
 *
 * ⛔ **장수에 단가를 곱하지 않는다**(`docs/DATA_DICTIONARY.md` 28.5 ②). 지금 앱이
 * `cutoutPrice(failed.length)` 로 곱하고 있고 그것이 이 함수가 생긴 이유다. 단가는 **장마다
 * 다를 수 있다**(다시 만들기가 낀 작업). 곱하면 그런 작업에서 틀린다.
 *
 * ⛔ **모르는 장을 `0` 으로 세지 않는다.** `0` 은 "돌아갈 돈이 없다"라는 **거짓 문장**이 되고,
 * 그 장은 청구된 적이 없을 뿐 셀러에게는 여전히 못 만든 장이다(28.3 ③).
 *
 * ⭐ **그래서 합과 함께 "금액을 모르는 장 수"를 돌려준다 (판정 3. 2026-09-21).**
 * 모르는 장을 합에서 빼면 화면이 **`못 만든 2장 · ₩500`** 처럼 **장수와 금액이 안 맞는 줄**을
 * 그리게 된다. 그 줄을 어떻게 적을지는 **낱말 문제라 `design-manager` 소관**이고, 이 함수가
 * 할 일은 **화면이 그 상태를 알아볼 수 있게 하는 것**까지다. 숫자 하나만 돌려주면 화면은
 * 그 상태를 **알 길이 없다.**
 *   - `unknownCount === 0` . 합이 전부다. 그대로 적는다
 *   - `0 < unknownCount < imageCount` . 일부만 금액을 안다
 *   - `unknownCount === imageCount` . **합이 `0` 인데 `₩0` 이 아니다.** ⛔ 이때 `₩0` 을
 *     적으면 안 되고, 무엇을 적을지는 화면 결정이다
 *
 * ⚠️ **`undefined`(서버가 아직 안 준다)와 `null`(서버가 모른다)을 가르지 않는다.** 뜻은
 * 다르지만 **이 자리에 적을 숫자가 없다는 결과가 같다.** 가르면 화면이 이행기 사정을 알아야
 * 하고, 그것은 서버가 칸을 채우는 날 사라질 지식이다. */
export function refundableTotal(job: DetailJobView): RefundableTotal {
  const images = refundableImages(job);
  let amount = 0;
  let unknownCount = 0;
  for (const image of images) {
    const value = image.refundableAmount;
    if (typeof value === "number") amount += value;
    else unknownCount += 1;
  }
  return { amount, imageCount: images.length, unknownCount };
}

/** **지금 이 장의 공급자 호출이 막혀 있는가. 막혔다면 왜인가.** `null` 이면 막히지 않았다.
 *
 * **서버 값이 정본이고 이 함수는 그것을 그대로 통과시킨다.** `image.recallBlockedReason` 이
 * 있으면 **파생하지 않고 그 값을 낸다.** 공급자가 지금 살아 있는지는 화면이 알 수 없다
 * (PRD `7.13` D-77 ② "판정 주체는 서버다").
 *
 * **서버가 아직 그 칸을 안 채우는 동안**의 폴백은 `design-manager` 가 명시로 허용했다
 * (PRD `7.6.8a`): **"서버가 그 값을 줄 때까지 화면은 자기가 아는 것으로만 낸다: 그 장의 실패
 * 코드 셋과 이 작업에 지금 만드는 중인 장이 있는가."** 그 폴백을 **여기 한 곳에만** 둔다.
 * 앱과 웹이 각자 쓰면 같은 장이 두 화면에서 다른 시트를 연다.
 *
 * ⛔ **장애를 추측하지 않는다.** `PROVIDER_FAILED`·`PROCESSING_INTERRUPTED`·
 * `PROVIDER_REJECTED` 는 **`null`** 이다. 앞의 둘은 다시 걸면 될 수도 있고
 * `PROVIDER_REJECTED` 는 **`A-25b` + "다시 찍는 편이 낫습니다"** 로 가는 것이 이미 확정이다
 * (`7.6.13b`). **모르면 `A-25b` 이고, 눌러서 막히면 그 시트의 실패 갈래가 받는다**(`7.6.8a`).
 * 못 띄워서 잃는 것은 셀러의 헛클릭 한 번(돈은 들지 않는다)이고, 잘못 띄우면 **되는 일을
 * 안 된다고 적는다.**
 *
 * ⛔ **처리 중을 사유로 내지 않는다** (`domain.ts` `RecallBlockReason` 의 판정 2).
 * `canRemakeImage`·`canRetryImage` 가 이미 버튼을 잠근다. 여기서 또 내면 **같은 사실을 두
 * 자리에서 판정**하고, 몇 초 뒤 끝날 일에 `다시 되는 때 / 미정` 을 적게 된다.
 *
 * **`JOB_DISCARDED` 가 실패 코드보다 앞선다.** 파기된 작업은 그 장 하나가 아니라 **밑천이
 * 통째로 없다.** 같은 상태를 `SOURCE_GONE` 으로 적으면 화면이 "이 장만 다시 올리면 된다"로
 * 읽는다. 화면 글자도 갈려 있다. 앱 `A-25c` · 웹 `BlockedPanel` 은 이 사유에
 * `REVIVE_BLOCKED_COPY.discarded`(`remake-messages.ts`)를 그린다. `sourceGone` 이 아니다.
 *
 * ⭐ **`now` 를 넘기면 기한 만료(`RETENTION_EXPIRED`)도 낸다.**
 * ⛔ **넘기지 않으면 안 낸다.** 그 경우 `canRemakeImage` 도 기한을 안 보므로 두 판정이
 * 갈리지 않는다. ⚠️ **넘겨야 하는 이유는 따로 있다.** 기한이 지난 장에서 `now` 를 빠뜨리면
 * **버튼은 사라지는데 시트가 아무 말도 못 한다**(`canRemakeImage` 는 `now` 없이도 값이
 * 있으면 막는다). 셀러는 "왜 없어졌지"만 남는다. **두 함수에 같은 `now` 를 넘겨라.**
 *
 * **`JOB_DISCARDED` 다음, 실패 코드보다 앞이다.** 파기는 작업 전체라 더 넓고, 기한은
 * 실패 코드보다 **더 최종적이다.** `PROVIDER_REJECTED` 로 실패한 장의 기한이 지났으면
 * 할 말은 색 대비가 아니라 기한이다(`SOURCE_GONE` 이 실패 코드보다 앞서는 것과 같은 결). */
export function recallBlockOf(
  image: Pick<JobImageView, "recallBlockedReason" | "errorCode" | "sourceRetentionExpiresAt">,
  job?: Pick<DetailJobView, "status">,
  now?: D1DateTime | null,
): RecallBlockReason | null {
  if (image.recallBlockedReason) return image.recallBlockedReason;
  if (job && isJobDiscarded(job)) return "JOB_DISCARDED";
  // ⛔ `now` 가 없으면 이 줄은 언제나 거짓이다(`isSourceRetentionExpired` 의 첫 갈래가
  //    아니라 둘째 갈래로 떨어지지 않게, 여기서는 `now` 유무를 먼저 본다). 서버가 축을
  //    싣기 전의 화면이 없는 사유를 지어내지 않게 하려는 것이다.
  if (typeof now === "string" && now !== "" && isSourceRetentionExpired(image, now)) {
    return "RETENTION_EXPIRED";
  }
  switch (image.errorCode) {
    case "NOT_CONFIGURED":
      return "PROVIDER_UNAVAILABLE";
    case "SOURCE_MISSING":
      return "SOURCE_GONE";
    case "SOURCE_TOO_LARGE":
      return "SOURCE_TOO_LARGE";
    default:
      return null;
  }
}

/** 막힌 사유의 **성격**. 화면이 `A-25c` 의 갈래를 고르는 입력이다.
 *
 * **낱말이 아니라 성격만 답한다.** 무슨 문장을 적을지는 `design-manager` 소관이고
 * (PRD `7.6.8a` 의 최종 문자열 표), 이 함수는 **그 표의 어느 열인지**까지만 말한다.
 *
 * - `TRANSIENT` . 갈래 **ⓘ 막혀 있다**. 기다리면 풀린다. `다시 되는 때 / 미정` 줄이 선다
 * - `TERMINAL` . 갈래 **ⓘⓘ 보낼 사진이 없다**. 그 사진으로는 영영이다. ⛔ `다시 되는 때`
 *   줄을 세우지 않는다. **모르는 것과 없는 것을 같은 배지로 적지 않는다**
 * - `BUSY` . ⛔ **시트를 열지 않는다.** 버튼 잠금 축이다(판정 2)
 * - `UNKNOWN` . 서버가 이 판이 모르는 새 사유를 내렸다. ⛔ **어느 갈래로 그릴지 여기서
 *   고르지 않는다.** 추측해서 ⓘ 로 그리면 오지 않을 날을 기다리게 하고, ⓘⓘ 로 그리면 곧
 *   풀릴 일을 영영이라고 적는다. **화면 결정이고 `design-manager` 소관이다**
 *
 * `null` 은 막히지 않았다는 뜻이다(`A-25`/`A-25b`). */
export type RecallBlockKind = "TRANSIENT" | "TERMINAL" | "BUSY" | "UNKNOWN";

export function recallBlockKind(
  reason: RecallBlockReason | null | undefined,
): RecallBlockKind | null {
  if (!reason) return null;
  switch (reason) {
    case "PROVIDER_UNAVAILABLE":
      return "TRANSIENT";
    case "SOURCE_GONE":
    case "SOURCE_TOO_LARGE":
    case "JOB_DISCARDED":
    // ⭐ 기한 만료는 기다려서 풀리지 않는다. 지난 날은 돌아오지 않는다.
    // ⛔ `TRANSIENT` 로 두면 `다시 되는 때 / 미정` 이 서고, 오지 않을 날을 기다리게 된다.
    case "RETENTION_EXPIRED":
      return "TERMINAL";
    case "JOB_PROCESSING":
      return "BUSY";
    default:
      return "UNKNOWN";
  }
}
